import React, {useEffect, useRef, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api, API_BASE, getCookie} from '../../api';

const STATUS_STYLES = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  ACCEPTED: 'bg-green-100 text-green-700',
  DECLINED: 'bg-red-100 text-red-700',
};

export default function EstimateDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [est, setEst] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);
  const [contact, setContact] = useState(null);
  const [company, setCompany] = useState(null);

  const load = () => api(`/estimates/${id}`).then(r => r.ok ? r.json() : null).then(e => {
    setEst(e);
    if (e?.contactId) api(`/contacts/${e.contactId}`).then(r => r.ok ? r.json() : null).then(setContact);
    else setContact(null);
    if (e?.companyId) api(`/companies/${e.companyId}`).then(r => r.ok ? r.json() : null).then(setCompany);
    else setCompany(null);
  });
  const loadAttachments = () => api(`/estimates/${id}/attachments`).then(r => r.ok ? r.json() : []).then(setAttachments);
  const loadNotes = () => api(`/estimates/${id}/notes`).then(r => r.ok ? r.json() : []).then(setNotes);
  const loadActivity = () => api(`/estimates/${id}/activity`).then(r => r.ok ? r.json() : []).then(setActivity);

  useEffect(() => { load(); loadAttachments(); loadNotes(); loadActivity(); }, [id]);

  const handleAction = async (action) => {
    const res = await api(`/estimates/${id}/${action}`, {method: 'POST'});
    if (res.ok) { load(); loadActivity(); }
  };

  const handleDelete = async () => {
    await api(`/estimates/${id}`, {method: 'DELETE'});
    navigate('/estimates');
  };

  const handlePreviewPdf = async () => {
    const res = await api(`/estimates/${id}/pdf`);
    if (res.ok) {
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewOpen(true);
    }
  };

  const handleDownloadPdf = async () => {
    const res = await api(`/estimates/${id}/pdf`);
    if (res.ok) {
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${est.estimateNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    await fetch(`${API_BASE}/estimates/${id}/attachments`, {method: 'POST', headers, body: formData});
    fileRef.current.value = '';
    setUploading(false);
    loadAttachments();
    loadActivity();
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    const res = await api(`/estimates/${id}/attachments/${attachmentId}`);
    if (res.ok) {
      const data = await res.json();
      const a = document.createElement('a');
      a.href = data.url;
      a.download = fileName;
      a.click();
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    await api(`/estimates/${id}/attachments/${attachmentId}`, {method: 'DELETE'});
    loadAttachments();
    loadActivity();
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    await api(`/estimates/${id}/notes`, {method: 'POST', body: JSON.stringify({content: noteText.trim()})});
    setNoteText('');
    loadNotes();
    loadActivity();
  };

  const handleDeleteNote = async (noteId) => {
    await api(`/estimates/${id}/notes/${noteId}`, {method: 'DELETE'});
    loadNotes();
  };

  if (!est) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/estimates" className="text-yellow-600 hover:text-yellow-800 text-sm">← Back to Estimates</Link>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-900">{est.title}</h2>
              <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${STATUS_STYLES[est.status] || ''}`}>{est.status}</span>
            </div>
            <p className="text-gray-500 text-sm mt-1">{est.estimateNumber} • {est.createdAt ? new Date(est.createdAt).toLocaleDateString() : ''}</p>
            {est.dealId && est.dealName && (
              <p className="text-gray-500 text-sm mt-1">Deal: <Link to={`/crm/deals/${est.dealId}`} className="text-blue-600 hover:text-blue-800">{est.dealName}</Link></p>
            )}
            {(est.companyName || est.contactName) && (
              <p className="text-gray-700 mt-1">{est.companyName}{est.companyName && est.contactName ? ' — ' : ''}{est.contactName}</p>
            )}
            {est.validUntil && <p className="text-gray-500 text-sm mt-1">Valid until {new Date(est.validUntil + 'T00:00:00').toLocaleDateString()}</p>}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={handlePreviewPdf} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Preview PDF</button>
            <button onClick={handleDownloadPdf} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Download PDF</button>
            {est.status === 'DRAFT' && (
              <>
                <Link to={`/estimates/${id}/edit`} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Edit</Link>
                <button onClick={() => handleAction('send')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Send</button>
                <button onClick={() => { setDeleteOpen(true); setDeleteConfirm(''); }} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm">Delete</button>
              </>
            )}
            {est.status === 'SENT' && (
              <>
                <button onClick={() => handleAction('accept')} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">Accept</button>
                <button onClick={() => handleAction('decline')} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">Decline</button>
              </>
            )}
            {est.status === 'ACCEPTED' && !est.jobId && (
              <button onClick={() => setConvertOpen(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm">Convert to Job</button>
            )}
            {est.jobId && (
              <a href={`/jobs/${est.jobId}`} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm">View Job</a>
            )}
          </div>
        </div>
      </div>

      {/* Customer Details */}
      {(contact || company) && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase mb-3">Customer</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {company && (
              <div>
                <p className="font-medium text-gray-900">{company.name}</p>
                {company.phone && <p className="text-sm text-gray-600 mt-1">{company.phone}</p>}
                {company.website && <p className="text-sm text-gray-600">{company.website}</p>}
                {company.address && <p className="text-sm text-gray-600">{company.address}</p>}
              </div>
            )}
            {contact && (
              <div>
                <p className="font-medium text-gray-900">{contact.firstName} {contact.lastName}</p>
                {contact.emails?.length > 0 && <p className="text-sm text-gray-600 mt-1">{contact.emails[0].email}</p>}
                {contact.phones?.length > 0 && <p className="text-sm text-gray-600">{contact.phones[0].phone}</p>}
                {contact.addresses?.length > 0 && (
                  <div className="text-sm text-gray-600 mt-1">
                    {contact.addresses[0].line1 && <p>{contact.addresses[0].line1}</p>}
                    {contact.addresses[0].line2 && <p>{contact.addresses[0].line2}</p>}
                    {contact.addresses[0].city || contact.addresses[0].state || contact.addresses[0].zip ? (
                      <p>{[contact.addresses[0].city, contact.addresses[0].state].filter(Boolean).join(', ')}{contact.addresses[0].zip ? ' ' + contact.addresses[0].zip : ''}</p>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Line Items */}
      <div className="bg-white rounded-lg shadow overflow-x-auto mb-6">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {est.lineItems?.map(li => (
              <tr key={li.id}>
                <td className="px-6 py-4 text-gray-900">{li.description}</td>
                <td className="px-6 py-4 text-gray-600 text-right">{li.quantity}</td>
                <td className="px-6 py-4 text-gray-600 text-right">${li.unitPrice?.toFixed(2)}</td>
                <td className="px-6 py-4 text-gray-900 font-medium text-right">${li.amount?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2">
              <td colSpan={3} className="px-6 py-4 text-right font-bold text-gray-900">Total</td>
              <td className="px-6 py-4 text-right font-bold text-gray-900 text-lg">${est.total?.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Customer Notes */}
      {est.notes && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Customer Notes</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{est.notes}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attachments */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500 uppercase">Attachments</h3>
            <label className={`px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
              {uploading ? 'Uploading...' : '+ Upload'}
              <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          </div>
          {attachments.length === 0 && <p className="text-gray-400 text-sm">No attachments</p>}
          <div className="space-y-2">
            {attachments.map(a => (
              <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <button onClick={() => handleDownloadAttachment(a.id, a.fileName)} className="text-sm text-yellow-600 hover:text-yellow-800 truncate flex-1 text-left">
                  {a.fileName}
                </button>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className="text-xs text-gray-400">{a.size ? (a.size < 1024*1024 ? (a.size/1024).toFixed(0)+'KB' : (a.size/1024/1024).toFixed(1)+'MB') : ''}</span>
                  <button onClick={() => handleDeleteAttachment(a.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Internal Notes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">Internal Notes</h3>
          <form onSubmit={handleAddNote} className="flex gap-2 mb-4">
            <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none text-sm" />
            <button type="submit" disabled={!noteText.trim()} className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 text-sm">Add</button>
          </form>
          {notes.length === 0 && <p className="text-gray-400 text-sm">No notes</p>}
          <div className="space-y-3">
            {notes.map(n => (
              <div key={n.id} className="border-b last:border-0 pb-3">
                <p className="text-gray-700 text-sm">{n.content}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-400">{n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}</span>
                  <button onClick={() => handleDeleteNote(n.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activity Log */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">Activity</h3>
        {activity.length === 0 && <p className="text-gray-400 text-sm">No activity</p>}
        <div className="space-y-3">
          {activity.map(a => (
            <div key={a.id} className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-yellow-400 mt-1.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-700">{a.detail}</p>
                <p className="text-xs text-gray-400">{a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {previewOpen && previewUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setPreviewOpen(false); URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{est.estimateNumber} — PDF Preview</h3>
              <button onClick={() => { setPreviewOpen(false); URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe src={previewUrl} className="w-full h-full border-0" title="PDF Preview" />
            </div>
          </div>
        </div>
      )}

      {convertOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConvertOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Convert to Job</h3>
            <p className="text-gray-600 mb-2">This will create a new job from <span className="font-semibold">{est.estimateNumber}</span> with the same customer, line items, and notes.</p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm text-gray-700 space-y-1">
              <p><span className="font-medium">Title:</span> {est.title}</p>
              {est.companyName && <p><span className="font-medium">Company:</span> {est.companyName}</p>}
              {est.contactName && <p><span className="font-medium">Contact:</span> {est.contactName}</p>}
              <p><span className="font-medium">Total:</span> ${est.total?.toFixed(2)}</p>
              <p><span className="font-medium">Line items:</span> {est.lineItems?.length || 0}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConvertOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={async () => { setConvertOpen(false); const res = await api(`/jobs/from-estimate/${id}`, {method: 'POST'}); if (res.ok) { const job = await res.json(); window.location.href = `/jobs/${job.id}`; } }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">Convert</button>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Estimate</h3>
            <p className="text-gray-600 mb-4">This will permanently delete <span className="font-semibold">{est.estimateNumber}</span> and all its data. This action cannot be undone.</p>
            <p className="text-sm text-gray-600 mb-2">Type <span className="font-mono font-bold text-red-600">delete</span> to confirm:</p>
            <input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} autoFocus
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setDeleteOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleteConfirm !== 'delete'}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">Delete Estimate</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
